import { createContext, useContext, useEffect } from 'react';
import type { ReactNode } from 'react';
import { useIntl } from 'react-intl';
import * as stylex from '@stylexjs/stylex';
import '@astryxdesign/core/theme/tokens.stylex';
import { colorVars, fontWeightVars, radiusVars, spacingVars, typeScaleVars } from '@astryxdesign/core/theme/tokens.stylex';
import { HStack } from '@astryxdesign/core/HStack';
import { Button } from '@astryxdesign/core/Button';
import { Icon } from '@astryxdesign/core/Icon';
import { useMediaQuery } from '@astryxdesign/core/hooks';
import type { LucideIcon } from 'lucide-react';
import { QronLogo } from '@qronstat/ui';
import { usePreferences } from '../app/preferences';
import { mobileQuery } from '../app/layout';
import { checkForUpdate } from '../app/pwa';
import mascotUrl from './mascot.webp';
import peekUrl from './mascot-peek.webp';
const mascotAspect = 536 / 884;
const mascotAnchorX = 0.2724;
const mascotAnchorY = 0.3043;
const mascotHeight = 'var(--qs-mascot-h)';
const wideQuery = '@media (min-width: 1024px)';
const peekAspect = 615 / 606;
const peekCut = 519 / 606;
const peekWidth = 'var(--qs-peek-w)';
const shortQuery = '@media (max-height: 599.98px)';
const backdrop = {
  backgroundColor: colorVars['--color-background-body'],
  backgroundImage: `radial-gradient(120% 60% at 50% 0%, ${colorVars['--color-accent-muted']}, transparent 70%), linear-gradient(var(--qs-hud-grid) 1px, transparent 1px), linear-gradient(90deg, var(--qs-hud-grid) 1px, transparent 1px)`,
  backgroundSize: '100% 100%, 48px 48px, 48px 48px'
} as const;
const MobileAuth = createContext(false);
export function useAuthMobile(): boolean {
  return useContext(MobileAuth);
}
export function AuthForm({
  onSubmit,
  children
}: {
  onSubmit: () => void;
  children: ReactNode;
}) {
  return <form className="x78zum5 xdt5ytf x18g69wz x1ghz6dp xv1oleu xocbrkx xm5u2gy xja4o2g" onSubmit={e => {
    e.preventDefault();
    onSubmit();
  }}>
      {children}
    </form>;
}
export function AuthActions({
  children
}: {
  children: ReactNode;
}) {
  const mobile = useAuthMobile();
  return <div {...{
    0: {
      className: "x78zum5 xdt5ytf x1txdalj"
    },
    1: {
      className: "x78zum5 xdt5ytf x1txdalj x1xye8es"
    }
  }[!!mobile << 0]}>{children}</div>;
}
export function AuthLayout({
  title,
  subtitle,
  icon,
  children
}: {
  title: string;
  subtitle?: string;
  icon?: LucideIcon;
  children: ReactNode;
}) {
  const intl = useIntl();
  const mobile = useMediaQuery(mobileQuery);
  const {
    locale,
    setLocale
  } = usePreferences();
  useEffect(() => checkForUpdate(), []);
  const language = <Button variant="ghost" size="sm" label={intl.formatMessage({
    id: 'auth.switchLanguage'
  })} onClick={() => setLocale(locale === 'ru' ? 'en' : 'ru')} />;
  const hero = <div className="x78zum5 xdt5ytf xjcht0a">
      {icon ? <span className="x3nfvp2 x6s0dn4 xl56j7k x100vrsf x1vqgdyp xjspbzw xgcxg3y xjse4m1" aria-hidden="true">
          <Icon icon={icon} />
        </span> : null}
      <h1 className="x1ghz6dp x1yego12 xh0iwvy x2mo6ok xjat59b x1tgivj0">{title}</h1>
      {subtitle ? <p className="x1ghz6dp xjm74w1 xw6l6zx xv1l7n4">{subtitle}</p> : null}
    </div>;
  if (mobile) {
    return <MobileAuth.Provider value>
        <main className="x1s41dwp x1ov3xa9 x78zum5 xdt5ytf x18g69wz x9f619 x1rj3w00 x11ejma8 xzz68wx x1eiddq6 x1rkz16d x1f6j0bd">
          <HStack hAlign="between" vAlign="center">
            <QronLogo framed size={30} name={intl.formatMessage({
            id: 'app.name'
          })} />
            {language}
          </HStack>
          <div className="x78zum5 xdt5ytf xl56j7k x1qh66ti x1iyjqo2 xh8yej3 x17fpy1y xvueqy4 x161nduv">
            <div className="x1n2onr6 x18grcl6 x1v7qe06">
              <img src={peekUrl} alt="" aria-hidden="true" draggable={false} className="x1lliihq xlvn2xd x10l6tqk x1vjfegm x13vifvy x1nrll8i x18vszg4 x14u7rrp xjd4byp x47corl x87ps6o" />
              <section className="x1n2onr6 x78zum5 xdt5ytf x9f619 x1hviunn xmkeg23 x1y0btm7 xmjh2ps xxkj8df x1dmgsgi xaawpy0 x9mgr7n x1ikrqgu">
                {hero}
                <div className="x78zum5 xdt5ytf">{children}</div>
              </section>
            </div>
          </div>
        </main>
      </MobileAuth.Provider>;
  }
  return <main className="x1eiddq6 x1rkz16d x1f6j0bd xqmv0gi x1ov3xa9 x78zum5 xdt5ytf x9f619 xq6koh6 x6xnjw4">
      <HStack hAlign="between" vAlign="center">
        <QronLogo framed size={30} name={intl.formatMessage({
        id: 'app.name'
      })} />
        {language}
      </HStack>
      <div className="x1iyjqo2 x78zum5 x6s0dn4 xl56j7k xq6koh6">
        <div className="x1n2onr6 xh8yej3 xp73w0s xdj266r xgwwqyz x1yf7rl7 xqaepf">
          <section className="x1n2onr6 x78zum5 xdt5ytf x1qh66ti x9f619 x1bav0fz x1hviunn xmkeg23 x1y0btm7 xmjh2ps xxkj8df x1dmgsgi xaawpy0">
            {hero}
            <div className="x78zum5 xdt5ytf">{children}</div>
          </section>
          <img src={mascotUrl} alt="" aria-hidden="true" draggable={false} className="x1s85apg xzhf20h x10l6tqk x1vjfegm x1fwpgv5 xzewbmh xn6s2s8 x1ty17x0 x47corl x87ps6o" />
        </div>
      </div>
    </main>;
}